import { useEffect, useRef } from 'react';
import { useAppStore, setIsIngameCampaignOnlyOpened } from '../../../store/app/appStore';

export interface CampaignSelectionConfig<T extends {id: string}> {
  /** Whether this layout should be shown */
  shouldShow: boolean;
  /** The campaign ID from props */
  campaignID?: string;
  /** Available campaigns/items */
  items: T[];
  /** Currently active item ID */
  activeItemId: string | undefined;
  /** Function to set the active item */
  setActiveItem: (id: string) => void;
  /** Section name to set when showing this layout */
  sectionName: string;
  /** Debug prefix for console logs */
  debugPrefix: string;
  /** Function to get ID from an item */
  getItemId: (item: T) => string;
}

/**
 * Shared hook for campaign selection logic used by TournamentsOnlyLayout and PrizePopOnlyLayout
 *
 * Handles:
 * - Setting the appropriate section and view mode when the layout is shown
 * - Selecting a specific campaign by campaignID
 * - Auto-selecting the first campaign when no campaignID is provided
 * - Preventing re-selection after initial load
 */
export const useCampaignSelection = <T extends { id: string }>(
  config: CampaignSelectionConfig<T>,
) => {
  const {
    shouldShow,
    campaignID,
    items,
    activeItemId,
    setActiveItem,
    sectionName,
    debugPrefix,
    getItemId,
  } = config;

  const setSelectedSection = useAppStore((state) => state.setSelectedSection);
  const setViewMode = useAppStore((state) => state.setViewMode);
  const isIngameCampaignOnlyOpened = useAppStore((state) => state.isIngameCampaignOnlyOpened);

  // Track previous campaignID to detect changes
  const prevCampaignIDRef = useRef<string | undefined>(undefined);

  // Effect to handle campaign-only mode setup
  useEffect(() => {
    if (shouldShow) {
      // Switch to appropriate section in single view mode
      setSelectedSection(sectionName);
      setViewMode('single');
    }
  }, [shouldShow, setSelectedSection, setViewMode, sectionName, campaignID, debugPrefix]);

  // Effect to select campaign from campaignID - runs regardless of current activeeCampaignId
  useEffect(() => {
    // Skip if not in camapign only mode, no items loaded, or no campaignID
    if (!shouldShow || items.length === 0 || !campaignID) return;

    // Only handle each campaignID once 
    if(prevCampaignIDRef.current === campaignID) return;

    // If campaignID is provided and it's different from the current active campaign, navigate to it
    const targetItem = items.find((item) => getItemId(item) === campaignID);
    if (targetItem) {
      //console.log(`[${debugPrefix}] Selecting campaign from campaignID: ${campaignID}`);
      setActiveItem(campaignID);
      prevCampaignIDRef.current = campaignID;
    }

    // Set the isOpened flag in the store after the initial load
    setIsIngameCampaignOnlyOpened(true);
  }, [
    shouldShow,
    campaignID,
    items,
    activeItemId,
    isIngameCampaignOnlyOpened,
    setActiveItem,
    debugPrefix,
    getItemId,
  ]);

  // Effect to auto-select first campaign when no campaignID is provided
  // and no campaign is currently active
  useEffect(() => {
    // Only auto-select first campaign if:
    // - shouldShow is enabled
    // - items are loaded
    // - no campaign is currently active
    // - no campaignID is provided (campaignID selection is handled by the effect above)
    if (shouldShow && items.length > 0 && !activeItemId && !campaignID) {
      //console.log(`[${debugPrefix}] Auto-selecting first campaign: ${items[0].id}`);
      setActiveItem(items[0].id);
    }
  }, [shouldShow, campaignID, items, activeItemId, setActiveItem, debugPrefix, getItemId]);
};
